import { useId } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { cn } from './cn';
import { ChevronDown } from './icons';

export interface SelectOption<TValue extends string> {
  value: TValue;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<TValue extends string> {
  label: string;
  value: TValue;
  onChange: (value: TValue) => void;
  options: readonly SelectOption<TValue>[];
  /** Hint rendered under the control and wired to `aria-describedby`. */
  description?: string;
  id?: string;
  name?: string;
  /** Keeps the label out of the layout but still on the control. */
  hideLabel?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * A styled native `<select>`.
 *
 * Native is the right answer here: the platform picker is the one users already
 * know, it behaves correctly on touch, and it needs no keyboard code at all.
 */
export function Select<TValue extends string>({
  label,
  value,
  onChange,
  options,
  description,
  id,
  name,
  hideLabel = false,
  disabled = false,
  className,
}: SelectProps<TValue>): ReactElement {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const descriptionId = `${selectId}-description`;

  function handleChange(event: ChangeEvent<HTMLSelectElement>): void {
    // The only values the element can produce are the ones we rendered.
    onChange(event.target.value as TValue);
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {hideLabel ? null : (
        <label htmlFor={selectId} className="text-[13px] font-medium text-ink">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          name={name}
          value={value}
          disabled={disabled}
          aria-label={hideLabel ? label : undefined}
          aria-describedby={description ? descriptionId : undefined}
          onChange={handleChange}
          className={cn(
            'h-9 max-md:h-11 w-full appearance-none rounded-md border border-border bg-surface',
            'pl-2.5 pr-8 text-sm text-ink transition-colors duration-150 motion-reduce:transition-none',
            'hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-55',
          )}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
      </div>
      {description && (
        <p id={descriptionId} className="text-xs leading-4 text-ink-muted">
          {description}
        </p>
      )}
    </div>
  );
}
